import React, { useState, useRef, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { useLoadScript } from '@react-google-maps/api';

interface Location {
  name: string;
  exactAddress?: string;
  area?: string;
  city?: string;
  state?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface LocationAutofillProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (location: Location) => void;
  className?: string;
  placeholder?: string;
  showExactAddress?: boolean;
}

const libraries: ["places"] = ["places"];

const LocationAutofill: React.FC<LocationAutofillProps> = ({
  value,
  onChange,
  onSelect,
  className = '',
  placeholder = 'Enter address',
  showExactAddress = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (isLoaded && !autocompleteService.current) {
      autocompleteService.current = new google.maps.places.AutocompleteService();
      sessionToken.current = new google.maps.places.AutocompleteSessionToken();
      
      // Create a dummy div for PlacesService (required but not displayed)
      const dummyElement = document.createElement('div');
      placesService.current = new google.maps.places.PlacesService(dummyElement);
    }
  }, [isLoaded]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    onChange(input);

    if (input.length > 2 && autocompleteService.current && sessionToken.current) {
      // Restrict to Miami and New York areas
      const searchOptions: google.maps.places.AutocompletionRequest = {
        input,
        sessionToken: sessionToken.current,
        types: ['address'],
        componentRestrictions: { country: 'us' },
        bounds: new google.maps.LatLngBounds(
          // Southwest corner of search area
          new google.maps.LatLng(25.4, -80.5), // Miami area
          new google.maps.LatLng(41.0, -73.7)  // New York area
        ),
        strictBounds: true
      };

      autocompleteService.current.getPlacePredictions(
        searchOptions,
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            // Filter predictions to only include Miami and New York areas
            const filteredPredictions = predictions.filter(prediction => {
              const address = prediction.description.toLowerCase();
              return address.includes('miami') || 
                     address.includes('new york') || 
                     address.includes('ny') ||
                     address.includes('fl');
            });
            setPredictions(filteredPredictions);
            setIsOpen(true);
          } else {
            setPredictions([]);
            setIsOpen(false);
          }
        }
      );
    } else {
      setPredictions([]);
      setIsOpen(false);
    }
  };

  const handleSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    if (placesService.current && sessionToken.current) {
      placesService.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['geometry', 'address_components', 'formatted_address'],
          sessionToken: sessionToken.current
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const addressComponents = place.address_components || [];
            
            // Extract address components
            const streetNumber = addressComponents.find(c => c.types.includes('street_number'))?.long_name || '';
            const route = addressComponents.find(c => c.types.includes('route'))?.long_name || '';
            const neighborhood = addressComponents.find(c => c.types.includes('neighborhood'))?.long_name;
            const locality = addressComponents.find(c => c.types.includes('locality'))?.long_name;
            const state = addressComponents.find(c => c.types.includes('administrative_area_level_1'))?.short_name;

            const exactAddress = place.formatted_address || `${streetNumber} ${route}`;
            const area = neighborhood || locality;
            const displayLocation = `${locality}, ${state}`;

            onSelect({
              name: displayLocation,
              exactAddress,
              area,
              city: locality,
              state,
              coordinates: place.geometry?.location ? {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              } : undefined
            });
          }
        }
      );
    }
    
    onChange(prediction.structured_formatting.main_text);
    setIsOpen(false);
    
    // Generate new session token after selection
    sessionToken.current = new google.maps.places.AutocompleteSessionToken();
  };

  if (!isLoaded) {
    return (
      <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50">
        Loading...
      </div>
    );
  }

  if (loadError) {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-4 py-2 border border-gray-300 rounded-md ${className}`}
        placeholder={placeholder}
      />
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => value && setIsOpen(true)}
          placeholder={placeholder}
          className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 ${className}`}
          autoComplete="off"
        />
      </div>

      {isOpen && predictions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg">
          <ul className="max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
            {predictions.map((prediction) => (
              <li
                key={prediction.place_id}
                onClick={() => handleSelect(prediction)}
                className="cursor-pointer hover:bg-gray-100 px-4 py-2"
              >
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {prediction.structured_formatting.main_text}
                    </div>
                    <div className="text-sm text-gray-500">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LocationAutofill;